import autosize from "autosize";
import {
  type FormEvent,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import Autosuggest, { type RenderInputComponentProps } from "react-autosuggest";
import { DialogComponent, HStack, Label, Popover, TriggerButton, TriggerButtonIntent, VStack } from "widgets";
import { useOverlay } from "widgets/hooks";

import { taggedUserIncludes } from "@/app-utils/filter.ts";
import { highlightStartsWith, pluralizeWord } from "@/app-utils/string.ts";
import { type Project, type ProjectActivity, type ProjectMember, type User } from "@/graphql.ts";
import { getProjectLabel } from "@/redux/modules/config.ts";
import { ENTITY_TYPE, FILE_TYPE } from "@/settings/settings.js";

import { Avatar } from "../core/UserAvatar.js";
import { AttachmentSelectPanel, type ISelectAttachmentOption } from "./AttachmentSelectPanel.js";

interface ICommentInputFormProps {
  onPostComment?: (
    comment: string,
    groupIds: number[],
    viewIds: number[],
    flagIds: number[],
    projectFileIds: number[],
  ) => Promise<ProjectActivity>;
  maxChars?: number;
  buttonLabel?: string;
  fieldLabel?: string;
  fieldPlaceholder?: string;
  project: Project;
  projectIsLoading: boolean;
  currentUser?: User;
}

export interface IUserSuggestion {
  type: SuggestionType;
  user?: User;
}

enum SuggestionType {
  TagAll = 1,
  TagUser = 2,
}

/**
 * A text field - button combination.
 * Used for posting of comments in the ProjectActivityPanel of a project overview page.
 * Shows the remaining number of characters if maxChars>0.
 *
 * TODO use InputFieldWithUserTagging and consolidate
 *
 * created on 12/1/2016
 */
export function CommentInputForm(props: ICommentInputFormProps) {
  const autosuggestRef = useRef<Autosuggest>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cursorPositionRef = useRef<number>(0);
  const attachmentsButtonRef = useRef<HTMLButtonElement>(null);
  const { overlayProps, state, triggerProps } = useOverlay();

  const [comment, setComment] = useState("");
  const [selectedAttachments, setSelectedAttachments] = useState<ISelectAttachmentOption[]>([]);
  const [suggestions, setSuggestions] = useState<IUserSuggestion[]>([]);

  const handlePostComment = useCallback(() => {
    const trimmedComment = comment.trim();

    if (trimmedComment.length && props.onPostComment) {
      const tokens: ISelectAttachmentOption[] = selectedAttachments;
      let token: ISelectAttachmentOption;
      const groupIds: number[] = [];
      const viewIds: number[] = [];
      const flagIds: number[] = [];
      const projectFileIds: number[] = [];

      for (token of tokens) {
        switch (token.type) {
          case ENTITY_TYPE.GENE:
          case ENTITY_TYPE.TRANSCRIPT:
          case ENTITY_TYPE.PROTEIN:
          case ENTITY_TYPE.ORGANISM:
          case ENTITY_TYPE.MUTATION:
          case ENTITY_TYPE.SAMPLE:
          case ENTITY_TYPE.VCF_VARIANT:
            groupIds.push(Number(token.id));
            break;
          case ENTITY_TYPE.VIEW:
            viewIds.push(Number(token.id));
            break;
          case ENTITY_TYPE.REGION_FLAG:
            flagIds.push(Number(token.id));
            break;
          case FILE_TYPE:
            projectFileIds.push(Number(token.id));
            break;
          default:
            throw Error(`Posting of comment with attachment of type ${token.type} is not supported`);
        }
      }
      props.onPostComment(trimmedComment, groupIds, viewIds, flagIds, projectFileIds);

      setComment("");
      setSelectedAttachments([]);
      setSuggestions([]);
    }
  }, [comment, selectedAttachments, props]);

  const getSuggestions = useCallback(
    (token: string): IUserSuggestion[] => {
      const userSuggestions: IUserSuggestion[] = (props.project.projectMembers ?? [])
        .filter((m) => {
          // exclude the current user, you cannot suggest to yourself
          return m.user?.username !== props.currentUser?.username;
        })
        .filter((member: ProjectMember) => (member.user ? taggedUserIncludes(member.user, token) : false))
        .map((member: ProjectMember) => {
          return { type: SuggestionType.TagUser, user: member.user ?? undefined } satisfies IUserSuggestion;
        });

      // Add @all suggestion at the beginning of list
      if (userSuggestions.length > 1) {
        userSuggestions.unshift({ type: SuggestionType.TagAll });
      }

      return userSuggestions;
    },
    [props.project.projectMembers, props.currentUser],
  );

  const getCurrentTagWord = useCallback((str: string, caretPosition: number) => {
    let word;

    const left = str.slice(0, caretPosition).search(/\S+$/);
    const right = str.slice(caretPosition).search(/\s/);

    if (right < 0) {
      word = str.slice(left);
    } else {
      word = str.slice(left, right + caretPosition);
    }

    if (!word || word.trim().length < 1 || word[0] !== "@") {
      return null;
    }

    word = word.trim().toLowerCase().slice(1);

    return word;
  }, []);

  const getSuggestionValue = useCallback((suggestion: IUserSuggestion) => {
    return suggestion.type === SuggestionType.TagAll ? "all" : suggestion.user?.username || "";
  }, []);

  const renderSuggestion = useCallback(
    (suggestion: IUserSuggestion, { query }: { query: string }) => {
      if (suggestion.type === SuggestionType.TagAll) {
        // Beware: .users contains the current user. too!
        const otherMemberCount =
          (props.project.projectMembers ?? []).length > 0 ? (props.project.projectMembers ?? []).length - 1 : 0;
        const memberLabel = pluralizeWord("member", otherMemberCount);
        return (
          <span>
            <em>
              Notify all {otherMemberCount} other {getProjectLabel(false, false)} {memberLabel}
            </em>
          </span>
        );
      } else {
        const user = suggestion.user;
        if (!user) return null;

        const token = getCurrentTagWord(query, cursorPositionRef.current);
        const username: string = user.username ? highlightStartsWith(user.username, token || "") : "";
        const firstName: string = user.firstName ? highlightStartsWith(user.firstName, token || "") : "";
        const lastName: string = user.lastName ? highlightStartsWith(user.lastName, token || "") : "";
        const fullName: string = firstName + " " + lastName;
        const separator: ReactElement | null =
          user.firstName || user.lastName ? <span className="description">&ndash;</span> : null;

        return (
          <span>
            <div className="thumbnail-wrapper">
              <Avatar user={user} size={24} />
            </div>
            <span dangerouslySetInnerHTML={{ __html: username }} />
            {separator}
            <span dangerouslySetInnerHTML={{ __html: fullName }} className="description" />
          </span>
        );
      }
    },
    [props.project.projectMembers, getCurrentTagWord],
  );

  const onSuggestionsClearRequested = useCallback(() => {
    setSuggestions([]);
  }, []);

  const onSuggestionsFetchRequested = useCallback(
    ({ value }: { value: string }) => {
      // TODO better use shouldRenderSuggestions callback
      if (!value?.includes("@")) {
        return;
      }

      if (textareaRef.current) {
        const token = getCurrentTagWord(value, textareaRef.current.selectionStart);

        if (token !== null) {
          setSuggestions(getSuggestions(token));
        } else if (suggestions.length > 0) {
          setSuggestions([]);
        }
      }
    },
    [getCurrentTagWord, getSuggestions, suggestions.length],
  );

  const onSuggestionSelected = useCallback(
    (_event: SyntheticEvent, { suggestionValue }: { suggestionValue: string }) => {
      if (textareaRef.current) {
        const cursorPos = textareaRef.current.selectionStart;
        const tagStartIndex = comment.lastIndexOf("@", cursorPos) + 1;

        let tagEndIndex = comment.indexOf(" ", tagStartIndex);
        if (tagEndIndex < 0) {
          tagEndIndex = comment.length;
        }

        // add a blank after the suggestion value if there is none present
        const conditionalBlank: string = comment.substring(tagEndIndex, tagEndIndex + 1) === " " ? "" : " ";
        setComment(
          [comment.slice(0, tagStartIndex), suggestionValue, conditionalBlank, comment.slice(tagEndIndex)].join(""),
        );
      }
    },
    [comment],
  );

  const handleCursorPositionChange = useCallback((_event: FormEvent<HTMLElement>) => {
    if (textareaRef.current) {
      cursorPositionRef.current = textareaRef.current.selectionStart;
    }
  }, []);

  const handleKeyDown = useCallback(
    (event: FormEvent<HTMLElement>) => {
      if ((event as KeyboardEvent<HTMLElement>).key === "Enter" && suggestions.length > 0) {
        event.preventDefault();
      }
    },
    [suggestions.length],
  );

  const handleChange = useCallback(
    (event: FormEvent<HTMLElement>, { newValue, method }: { newValue: string; method: string }) => {
      if (suggestions.length > 0 && (method === "click" || method === "enter")) {
        // User has selected a suggestion via click or enter => delegate to select handler
        onSuggestionSelected(event, { suggestionValue: newValue });
        // move focus to textarea again
        textareaRef.current?.focus();
      } else {
        // normal typing within the textarea
        let text = (event.target as HTMLTextAreaElement).value;

        if (props.maxChars && props.maxChars > 0) {
          text = text.substring(0, props.maxChars);
        }

        setComment(text);
      }
    },
    [suggestions.length, onSuggestionSelected, props.maxChars],
  );

  const renderSuggestionsContainer = useCallback(
    ({ children, containerProps }: { children: ReactNode; containerProps: HTMLAttributes<HTMLDivElement> }) => {
      if (children) {
        return (
          // 26px because react-autosuggest has a global style I don't want to break
          <div {...containerProps} style={{ ...containerProps.style, transform: "translateY(26px)" }}>
            {children}
          </div>
        );
      }
      return <div></div>;
    },
    [],
  );

  const inputProps = {
    id: "project-comment-autosuggest",
    value: comment,
    onChange: handleChange,
    onKeyPress: handleCursorPositionChange,
    onKeyDown: handleKeyDown,
    placeholder: props.fieldPlaceholder || "",
    rows: 2, //  minimum height is 2 rows, set for react-autosize-textarea package (see method renderInputComponent)
    style: { maxHeight: 150 },
  };

  useEffect(() => {
    if (textareaRef.current) {
      autosize(textareaRef.current);
    }
  }, []);

  const { maxChars, fieldLabel, buttonLabel, project, projectIsLoading } = props;
  const renderInputComponent = useCallback(
    (inputProps: RenderInputComponentProps) => {
      const tooltip =
        comment.length === 0
          ? "To post a comment, first enter some text into the comment field"
          : `Post your comment to the ${getProjectLabel(false, false)}`;

      return (
        <>
          <form role="form" onSubmit={handlePostComment}>
            <VStack style={{ width: "100%" }}>
              <HStack>
                <Label>{fieldLabel}</Label>
                <span style={{ marginLeft: "auto", color: "var(--secondary-light)", fontSize: "11px" }}>
                  {maxChars && maxChars > 0 && comment.length > 0 ? (
                    <>{maxChars - comment.length} characters left </>
                  ) : (
                    <>&nbsp;</>
                  )}
                </span>
              </HStack>
              <span id="project-activity-comment-input" style={{ height: 0 }} />
              <HStack spacing={0} style={{ alignItems: "stretch" }}>
                <textarea
                  {...inputProps}
                  ref={textareaRef}
                  style={{
                    flex: "1 0 auto",
                    borderColor: "var(--ui-border)",
                    zIndex: 3,
                    ...inputProps.style,
                  }}
                />

                <TriggerButton
                  {...triggerProps}
                  ref={attachmentsButtonRef}
                  style={{ height: "auto", flex: "0 0 auto", marginLeft: -1, borderRadius: 0, zIndex: 2 }}
                  icon={"attachment"}
                  label={""}
                  badge={selectedAttachments.length}
                  intent={TriggerButtonIntent.Outlined}
                  tooltip="Attach Saved Items"
                />

                <TriggerButton
                  style={{ height: "auto", flex: "0 0 auto", marginLeft: -1, borderRadius: 0, zIndex: 1 }}
                  icon={"send"}
                  label={buttonLabel || "Post"}
                  onPress={handlePostComment}
                  isDisabled={comment.length === 0 || projectIsLoading}
                  intent={TriggerButtonIntent.Active}
                  tooltip={tooltip}
                >
                  {buttonLabel || "Post"}
                </TriggerButton>
              </HStack>
            </VStack>
            {project && !project.isPrivateWorkspace && (
              <div style={{ marginTop: 5, fontSize: "12px", color: "var(--secondary-light)" }}>
                Notify other {getProjectLabel(false, false)} members using @username notation
              </div>
            )}
          </form>
          <Popover {...overlayProps} state={state} triggerRef={attachmentsButtonRef}>
            <DialogComponent>
              <AttachmentSelectPanel
                selectedAttachments={selectedAttachments}
                projectId={project.id}
                onSelect={(value) => setSelectedAttachments(value || [])}
              />
            </DialogComponent>
          </Popover>
        </>
      );
    },
    [
      buttonLabel,
      comment.length,
      fieldLabel,
      handlePostComment,
      maxChars,
      overlayProps,
      project,
      projectIsLoading,
      selectedAttachments,
      state,
      triggerProps,
    ],
  );

  return (
    <div className="comment-input-form">
      <Autosuggest
        ref={autosuggestRef}
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        onSuggestionSelected={onSuggestionSelected}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        renderSuggestionsContainer={renderSuggestionsContainer}
        renderInputComponent={renderInputComponent}
        highlightFirstSuggestion={true}
        inputProps={inputProps}
      />
    </div>
  );
}
